import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { TrendingUp, TrendingDown, Calendar, AlertTriangle, Store } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

const MONTH_NAMES_ID = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
const CHART_COLORS = ['#14b8a6', '#38bdf8', '#818cf8', '#f59e0b', '#ec4899', '#a78bfa', '#6366f1', '#f97316'];

function formatCurrency(val) {
  if (val >= 1000000000) return `Rp${(val / 1000000000).toFixed(1)} M`;
  if (val >= 1000000) return `Rp${Math.round(val / 1000000)} jt`;
  if (val >= 1000) return `Rp${Math.round(val / 1000)} rb`;
  return `Rp${val.toLocaleString('id-ID')}`;
}

function formatValue(title, value) {
  if (title === 'REVENUE' || title === 'OPEX' || title === 'PROFIT') return formatCurrency(value);
  return value.toLocaleString('id-ID');
}

function KpiCard({ title, value, change, prevMonthLabel }) {
  const isPositive = change >= 0;
  const isNegativeTrend = (title === 'MEMBER EXPIRED' || title === 'OPEX');
  const trendGood = isNegativeTrend ? !isPositive : isPositive;
  const changeLabel = `${change >= 0 ? '+' : ''}${change}% vs ${prevMonthLabel}`;

  return (
    <div className="relative overflow-hidden rounded-sm border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.03),rgba(9,9,11,0.82))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-all duration-200 hover:border-white/20">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-1">{title}</p>
      <p className="text-3xl font-black tracking-tight text-white mb-2">{formatValue(title, value)}</p>
      <div className="flex items-center gap-1">
        {trendGood ? (
          <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
        ) : (
          <TrendingDown className="w-3.5 h-3.5 text-red-400" />
        )}
        <span className={`text-xs font-medium ${trendGood ? 'text-emerald-400' : 'text-red-400'}`}>
          {changeLabel}
        </span>
      </div>
      <MiniSparkline positive={trendGood} />
    </div>
  );
}

function MiniSparkline({ positive }) {
  const points = useMemo(() => {
    const pts = [];
    for (let i = 0; i < 12; i++) {
      pts.push({
        x: (i / 11) * 100,
        y: 20 + Math.sin(i * 0.6) * 10 + (positive ? -i * 0.8 : i * 0.8) + Math.random() * 4,
      });
    }
    return pts;
  }, [positive]);
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  return (
    <svg viewBox="0 0 100 40" className="absolute bottom-2 right-3 w-16 h-8 opacity-40">
      <path d={pathD} fill="none" stroke={positive ? '#14b8a6' : '#ef4444'} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function HourlyVisitsChart({ data }) {
  return (
    <div className="rounded-sm border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.03),rgba(9,9,11,0.82))] p-5">
      <h3 className="text-sm font-bold text-white tracking-tight mb-4">KUNJUNGAN PER JAM</h3>
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="hour" tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ background: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, fontSize: 12 }}
              labelStyle={{ color: '#A1A1AA' }}
              formatter={(value) => [value.toLocaleString('id-ID'), 'Kunjungan']}
            />
            <Bar dataKey="visits" fill="#14b8a6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function DonutChart({ data, title, format = 'currency' }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const fmt = format === 'currency' ? formatCurrency : (v) => v.toLocaleString('id-ID');

  return (
    <div className="rounded-sm border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.03),rgba(9,9,11,0.82))] p-5">
      <h3 className="text-sm font-bold text-white tracking-tight mb-4">{title}</h3>
      <div className="flex items-center gap-6">
        <div className="relative w-[160px] h-[160px] shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={48}
                outerRadius={72}
                paddingAngle={3}
                dataKey="value"
                stroke="none"
              >
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.color || CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[10px] text-zinc-500 uppercase">Total</span>
            <span className="text-sm font-bold text-white">{fmt(total)}</span>
          </div>
        </div>
        <div className="flex flex-col gap-2 min-w-0">
          {data.map((entry, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: entry.color || CHART_COLORS[i] }} />
              <span className="text-zinc-400 truncate">{entry.name}</span>
              <span className="text-zinc-600 ml-auto">{total > 0 ? Math.round((entry.value / total) * 100) : 0}%</span>
              <span className="text-zinc-500 ml-1 whitespace-nowrap">({fmt(entry.value)})</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EarlyWarnings({ warnings }) {
  return (
    <div className="rounded-sm border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.03),rgba(9,9,11,0.82))] p-5">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className="w-4 h-4 text-amber-400" />
        <h3 className="text-sm font-bold text-white tracking-tight uppercase">Early Warning</h3>
      </div>
      <div className="flex flex-col gap-3">
        {warnings.map((w, i) => {
          const isChurn = w.type === 'churn_risk';
          const isExpiring = w.type === 'expiring_7days';
          const isNegative = (w.type === 'electricity' && w.value > 0) || (w.type === 'revenue_growth' && w.value < 0);
          return (
            <div key={i} className="flex items-start gap-3 rounded-sm bg-white/[0.02] border border-white/5 px-3 py-2.5">
              <div className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                isChurn ? 'bg-red-500/20 text-red-400' :
                isExpiring ? 'bg-amber-500/20 text-amber-400' :
                isNegative ? 'bg-red-500/20 text-red-400' :
                'bg-emerald-500/20 text-emerald-400'
              }`}>
                {w.count !== undefined ? w.count : `${w.value}%`}
              </div>
              <p className="text-xs text-zinc-400 leading-relaxed">{w.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ExecutiveDashboard() {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [merchants, setMerchants] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMerchants();
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedMerchant]);

  const fetchMerchants = async () => {
    try {
      const { data: res } = await axios.get(`${API}/merchants`, { withCredentials: true });
      setMerchants(Array.isArray(res) ? res : []);
    } catch (error) {
      console.error('Failed to fetch merchants:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { month: selectedMonth };
      if (selectedMerchant) params.merchant_id = selectedMerchant;
      const { data: res } = await axios.get(`${API}/dashboard/executive-stats`, {
        params,
        withCredentials: true,
      });
      setData(res);
    } catch (error) {
      console.error('Failed to fetch executive stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    const curYear = now.getFullYear();
    const curMonth = now.getMonth();
    for (let i = 0; i < 12; i++) {
      const m = curMonth - i;
      const dt = new Date(curYear, m, 1);
      const val = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const label = `${MONTH_NAMES_ID[dt.getMonth()]} ${dt.getFullYear()}`;
      opts.push({ value: val, label });
    }
    return opts;
  }, []);

  if (loading || !data) {
    return (
      <DashboardLayout>
        <Header title="Executive Dashboard" />
        <div className="p-8 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const [year, month] = selectedMonth.split('-').map(Number);
  const prevDate = new Date(year, month - 2, 1);
  const prevMonthLabel = `${MONTH_NAMES_ID[prevDate.getMonth()]} ${prevDate.getFullYear()}`;

  return (
    <DashboardLayout>
      <Header title="Executive Dashboard" />
      <div className="p-8" data-testid="executive-dashboard-page">
        {/* Title & Month Picker */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-white tracking-tight">EXECUTIVE DASHBOARD</h1>
            <p className="text-xs text-zinc-500 mt-0.5">Ringkasan performa bisnis gym</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="appearance-none bg-zinc-900 border border-white/10 rounded-sm pl-9 pr-8 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/50 cursor-pointer"
              >
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 5L6 8L9 5" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
            </div>
            <div className="relative">
              <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
              <select
                value={selectedMerchant}
                onChange={(e) => setSelectedMerchant(e.target.value)}
                className="appearance-none bg-zinc-900 border border-white/10 rounded-sm pl-9 pr-8 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/50 cursor-pointer min-w-[160px]"
              >
                <option value="">Semua Cabang</option>
                {merchants.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 5L6 8L9 5" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4 mb-6">
          {data.kpis.map((kpi, i) => (
            <KpiCard key={i} title={kpi.title} value={kpi.value} change={kpi.change} prevMonthLabel={prevMonthLabel} />
          ))}
        </div>

        {/* Main Content: Charts + Early Warning */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 mb-6">
          {/* Charts Section */}
          <div className="xl:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6">
            <HourlyVisitsChart data={data.charts.visits_per_hour} />
            <DonutChart data={data.charts.member_engagement} title="MEMBER ENGAGEMENT" format="number" />
            <DonutChart data={data.charts.revenue_breakdown} title="REVENUE BREAKDOWN" />
            <DonutChart data={data.charts.opex_breakdown} title="OPEX BREAKDOWN" />
          </div>

          {/* Early Warning Panel */}
          <div className="xl:col-span-1">
            <EarlyWarnings warnings={data.early_warnings} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
